<template>
  <!-- TODO: replace animation class -->
  <transition-group
    tag="div"
    class="sdv-activation-preview-list"
    :enter-active-class="internalAnimation.enter"
    :leave-active-class="internalAnimation.leave"
    appear
  >
    <template
      v-for="({ activation, context, itemKey, itemClass }) in internalItems"
    >
      <slot
        name="item"
        :item="{ activation, context, itemKey, itemClass }"
        :on="listListeners"
      >
        <ActivationSmartblockList
          :key="itemKey"
          :class="itemClass"
          :activation="activation"
          :context="context"
          preview
          v-on="listListeners"
        />
      </slot>
    </template>
  </transition-group>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, unref, toRefs } from 'vue-demi'
import { ExperienceActivationContext, ExperienceActivationInstance } from 'sd-types/app/experience/activation'
import ActivationSmartblockList from './ActivationSmartblockList.vue'
import { ActivationListContext } from '../types'
import { defineActivationListEmits, useBypassActivationListEvents } from '../composables/use-activation-list-events'
import { merge } from 'lodash-es'
import { ActivationTemplate } from 'sd-types/models/activation'
import { useDefaultPreviewTemplate } from '../composables/internals/use-default-preview-template'
const emits = defineActivationListEmits()

export const props = {
  items: {
    type: Array as PropType<Array<{
      activation: ExperienceActivationInstance
      context?: ExperienceActivationContext
    }>>,
    required: false,
    default: () => []
  },
  defaultPreviewTemplate: {
    type: Array as PropType<ActivationTemplate[]>,
    required: false,
    default: () => []
  },
  options: {
    type: Object as PropType<ActivationListContext>,
    default: () => ({})
  },
  animation: {
    type: [Boolean, Object] as PropType<false | { enter: string, leave: string }>,
    required: false,
    default: undefined
  }
}

export default defineComponent({
  name: 'ActivationPreviewList',
  components: {
    ActivationSmartblockList
  },
  props,
  emits: {
    ...emits
  },
  setup (props, context) {
    const { defaultPreviewTemplate: defaultPreviewTemplateFromProp } = toRefs(props)
    const defaultPreviewTemplate = useDefaultPreviewTemplate(defaultPreviewTemplateFromProp)
    /**
     * FIXME create a bug in smartblocks DisplayRenderer, and remove this
     * taken from: https://github.com/Source-Digital/experience-engine/blob/079caf37557291ecb06ed3030027b74ac6180ff7/front/src/composables/use-smartblock-preview.ts#L8
     * Was initially added to support old activation format, but we should move to using context.
     * @deprecated
     */
    function mergeLegacyPreviewSettings (template: ActivationTemplate, _context?: ExperienceActivationContext) {
      /**
       * @deprecated use {@code ActivationContext} instead
       */
      const baseSettings = {
        label: {
          text: _context?.preview?.text
        },
        image: {
          src: _context?.preview?.icon
        }
      }
      return merge({}, baseSettings, template.settings)
    }

    const internalAnimation = computed((): { leave?: string, enter?: string } => {
      if (props.animation === false) return {}
      if (typeof props.animation === 'object') return props.animation
      const alignment = props.options.left === true ? 'left' : 'right'
      return {
        enter: `sdv-animate slide-in-${alignment}`,
        leave: `sdv-animate slide-out-${alignment}`
      }
    })
    const internalItems = computed(() => {
      return (props.items ?? []).map((item, index) => {
        const previewTemplate: ActivationTemplate[] = (item.activation.preview?.template ?? unref(defaultPreviewTemplate) ?? [])
        return {
          itemKey: item.activation.instance.id ?? index,
          activation: {
            ...item.activation,
            preview: {
              ...item.activation.preview,
              template: previewTemplate.map(i => {
                return {
                  ...i,
                  settings: mergeLegacyPreviewSettings(i, item.context)
                }
              })
            }
          },
          // FIXME loosen ExperienceActivationContext type
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          context: {
            ...(item.context ?? {}),
            ...(props.options ?? {}),
            isPreview: true
          } as ExperienceActivationContext,
          itemClass: 'sdv-activation-preview-list__item'
        }
      })
    })
    const listListeners = useBypassActivationListEvents(context)
    return {
      internalItems,
      internalAnimation,
      listListeners
    }
  }
})
</script>
<!-- Intentionally leaving this unscoped, so it can be overridden by external css. -->
<!-- DO NOT put classes that may collide with external CSS.-->
<style lang="postcss">
  @keyframes sdvSlideRight {
      0% {
        opacity: 0;
        max-height: 0;
        transform: translateX(-3em) scale(0.9);
        margin-top: 0;
        margin-bottom: 0;
      }

      100% {
        opacity: 1;
        max-height: 30vmax;
        transform: translateX(0) scale(1);
      }
    }

  @keyframes sdvSlideLeft {
        0% {
          opacity: 0;
          max-height: 0;
          transform: translateX(3em) scale(0.9);
          margin-top: 0;
          margin-bottom: 0;
        }

        100% {
          opacity: 1;
          max-height: 30vmax;
          transform: translateX(0) scale(1);
        }
      }
  .sdv-activation-preview-list {
    .sdv-animate {
      &.slide-in-right {
        animation: sdvSlideRight 1s;
      }
      &.slide-out-right {
        animation: sdvSlideRight 1s reverse;
      }

      &.slide-in-left {
        animation: sdvSlideLeft 1s;
      }
      &.slide-out-left {
        animation: sdvSlideLeft 1s reverse;
      }

    }
  }

</style>
